import React, { useState, useEffect, useCallback } from "react";
import { uploadFile } from "../../firebase/fileUpload";
import ParticipationAdditionalData from "../../components/eventAdditionalData/child/ParticipationAdditionalData";

const SponsorParticipationTab = ({
  ppData,
  onDataChange,
  isSponsorParticipationCarousel,
  setIsSponsorParticipationCarousel,
  sponsorParticipationCarouselConfig,
  setParticipationCarouselConfig,
}) => {
  const [isEnabled, setIsEnabled] = useState(ppData.isEnabled || false);
  const [ppCaption1, setPPCaption1] = useState(ppData.caption1 || "");
  const [ppCaption2, setPPCaption2] = useState(ppData.caption2 || "");
  const [ppHashtags, setPPHashtags] = useState(ppData.hashtags || []);
  const [frameImageUrl, setFrameImageUrl] = useState(ppData.frameImage || "");
  const [participationCarouselImages, setParticipationCarouselImages] =
    useState(ppData.carouselImages || []);

  const [isPPHashtagEditable, setIsPPHashtagEditable] = useState(
    ppData.isPPHashtagEditable || false
  );
  const [isPPCaptionEditable, setIsPPCaptionEditable] = useState(
    ppData.isPPCaptionEditable || false
  );
  const [arePPSponsorsTagEditable, setArePPSponsorsTagEditable] = useState(
    ppData.arePPSponsorsTagEditable || false
  );
  const [isDefaultChecked, setIsDefaultChecked] = useState(
    ppData.isDefaultChecked || false
  );
  const [newHashtag, setNewHashtag] = useState("");
  // isParticipationCarousel + participationCarouselConfig come in as controlled
  // props (wired to additionalData root in the parent form) – no local state needed.
  const [errors, setErrors] = useState({});

  // Sync to parent on every meaningful change
  useEffect(() => {
    if (typeof onDataChange === "function") {
      onDataChange({
        isEnabled,
        caption1: ppCaption1,
        caption2: ppCaption2,
        hashtags: ppHashtags,
        frameImage: frameImageUrl,
        carouselImages: participationCarouselImages,
        isPPHashtagEditable,
        isPPCaptionEditable,
        arePPSponsorsTagEditable,
        isDefaultChecked,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEnabled,
    ppCaption1,
    ppCaption2,
    ppHashtags,
    frameImageUrl,
    participationCarouselImages,
    isPPHashtagEditable,
    isPPCaptionEditable,
    arePPSponsorsTagEditable,
    isDefaultChecked,
  ]);

  const handleFileUpload = useCallback(async (file) => {
    if (!file) return null;
    try {
      return await uploadFile(file);
    } catch (e) {
      console.error("Upload failed:", e);
      return null;
    }
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-50">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            Participation Post Configuration
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure how sponsors interact with participant posts.
          </p>
        </div>
        <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">
            {isEnabled ? "Enabled" : "Disabled"}
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isEnabled}
              onChange={(e) => setIsEnabled(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-600"></div>
          </label>
        </div>
      </div>

      {!isEnabled ? (
        <div className="py-12 text-center bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.268 14c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            Sponsor Posts Disabled
          </h3>
          <p className="text-gray-500 max-w-sm mx-auto mt-2">
            Enable sponsor posts to configure captions, hashtags, and frames for
            your event sponsors.
          </p>
        </div>
      ) : (
        <>
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-8 flex gap-3">
            <div className="text-blue-500 mt-0.5">
              <svg
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-sm text-blue-800 leading-relaxed">
              This configuration applies to <strong>all sponsors</strong> who
              purchase any package for this event. Set the caption, hashtags,
              and post frame that sponsors will use when uploading their
              participation post.
            </p>
          </div>

          <ParticipationAdditionalData
            ppCaption1={ppCaption1}
            setPpCaption1={setPPCaption1}
            ppCaption2={ppCaption2}
            setPpCaption2={setPPCaption2}
            ppHashtags={ppHashtags}
            setPpHashtags={setPPHashtags}
            newHashtag={newHashtag}
            setNewHashtag={setNewHashtag}
            frameImageUrl={frameImageUrl}
            setFrameImageUrl={setFrameImageUrl}
            participationCarouselImages={participationCarouselImages}
            setParticipationCarouselImages={setParticipationCarouselImages}
            isPPHashtagEditable={isPPHashtagEditable}
            setIsPPHashtagEditable={setIsPPHashtagEditable}
            isPPCaptionEditable={isPPCaptionEditable}
            setIsPPCaptionEditable={setIsPPCaptionEditable}
            arePPSponsorsTagEditable={arePPSponsorsTagEditable}
            setArePPSponsorsTagEditable={setArePPSponsorsTagEditable}
            isDefaultChecked={isDefaultChecked}
            setIsDefaultChecked={setIsDefaultChecked}
            handleFileUpload={handleFileUpload}
            isParticipationCarousel={isSponsorParticipationCarousel}
            setIsParticipationCarousel={setIsSponsorParticipationCarousel}
            participationCarouselConfig={sponsorParticipationCarouselConfig}
            setParticipationCarouselConfig={setParticipationCarouselConfig}
            errors={errors}
            setErrors={setErrors}
          />
        </>
      )}
    </div>
  );
};

export default SponsorParticipationTab;
